import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useParams,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AppShell } from "./components/app-shell";
import { orpc, queryClient } from "./lib/api";
import { lastOrg, rememberOrg, requireSession } from "./lib/session";
import { AccountPage } from "./routes/account";
import { AdminCancellationsPage } from "./routes/admin/cancellations";
import { AdminLayout } from "./routes/admin/layout";
import { AdminLegalPage } from "./routes/admin/legal";
import {
  AdminAlertsPage,
  AdminAuditPage,
  AdminBackupsPage,
  AdminJobsPage,
} from "./routes/admin/ops";
import { AdminOrgPage, AdminOrgsPage } from "./routes/admin/orgs";
import { AdminOverviewPage } from "./routes/admin/overview";
import { AdminTrafficPage } from "./routes/admin/traffic";
import { AdminUserPage, AdminUsersPage } from "./routes/admin/users";
import {
  AuthLayout,
  ForgotPasswordPage,
  ResetPasswordPage,
  SignInPage,
  SignUpPage,
} from "./routes/auth";
import { BrandPage } from "./routes/brand";
import { CancellationPage } from "./routes/cancel";
import { DocumentPage } from "./routes/document";
import { AcceptInvitationPage, ConsentPage, OnboardingPage } from "./routes/flows";
import { ImprintPage, PrivacyPage, TermsPage, WithdrawalPage } from "./routes/legal";
import { MediaPage } from "./routes/media";
import { PlanningPage } from "./routes/planning";
import { ProjectPage, ProjectsPage } from "./routes/projects";
import { SettingsPage } from "./routes/settings";
import { SharePage } from "./routes/share";
import { TemplatesPage } from "./routes/templates";
import { TrashPage } from "./routes/trash";

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <Toaster richColors position="top-center" />
    </>
  ),
  notFoundComponent: () => (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
      <p className="text-4xl font-semibold text-zinc-300">404</p>
      <p className="text-zinc-600">Diese Seite gibt es nicht.</p>
      <a href="/" className="text-sm font-medium text-brand-600">
        Zur Startseite
      </a>
    </div>
  ),
});

// Public pages -------------------------------------------------------------
const authLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth",
  component: AuthLayout,
});
const signIn = createRoute({
  getParentRoute: () => authLayout,
  path: "/sign-in",
  component: SignInPage,
});
const signUp = createRoute({
  getParentRoute: () => authLayout,
  path: "/sign-up",
  component: SignUpPage,
});
const forgotPassword = createRoute({
  getParentRoute: () => authLayout,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});
const resetPassword = createRoute({
  getParentRoute: () => authLayout,
  path: "/reset-password",
  component: ResetPasswordPage,
});

// Legal pages, public ---------------------------------------------------------
const imprint = createRoute({
  getParentRoute: () => rootRoute,
  path: "/impressum",
  component: ImprintPage,
});
const privacy = createRoute({
  getParentRoute: () => rootRoute,
  path: "/datenschutz",
  component: PrivacyPage,
});
const terms = createRoute({
  getParentRoute: () => rootRoute,
  path: "/nutzungsbedingungen",
  component: TermsPage,
});
const withdrawal = createRoute({
  getParentRoute: () => rootRoute,
  path: "/widerruf",
  component: WithdrawalPage,
});
// "Verträge hier kündigen" (§ 312k BGB): public, no sign-in needed.
const cancellation = createRoute({
  getParentRoute: () => rootRoute,
  path: "/kuendigen",
  component: CancellationPage,
});

// Share links: a file without an account (docs/15-zusammenarbeit.md) -----------
const share = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s/$token",
  component: SharePage,
});

// Signed in, without organization context ----------------------------------
const cardLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "card",
  beforeLoad: ({ location }) => requireSession(location.href),
  component: AuthLayout,
});
const consent = createRoute({
  getParentRoute: () => cardLayout,
  path: "/oauth/consent",
  component: ConsentPage,
});
const acceptInvitation = createRoute({
  getParentRoute: () => cardLayout,
  path: "/accept-invitation/$invitationId",
  component: AcceptInvitationPage,
});
const onboarding = createRoute({
  getParentRoute: () => cardLayout,
  path: "/onboarding",
  component: OnboardingPage,
});

const appLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: ({ location }) => requireSession(location.href),
});
const account = createRoute({
  getParentRoute: () => appLayout,
  path: "/account",
  component: AccountPage,
});
const home = createRoute({
  getParentRoute: () => appLayout,
  path: "/",
  beforeLoad: async () => {
    const orgs = await queryClient.fetchQuery(orpc.orgs.list.queryOptions());
    if (orgs.length === 0) throw redirect({ to: "/onboarding" });
    const remembered = lastOrg();
    const orgId = orgs.find((org) => org.id === remembered)?.id ?? orgs[0]!.id;
    throw redirect({ to: "/o/$orgId", params: { orgId } });
  },
});

// Platform administration (access is checked by the server) -------------------
const admin = createRoute({
  getParentRoute: () => appLayout,
  path: "/admin",
  component: AdminLayout,
});
const adminRoutes = [
  createRoute({ getParentRoute: () => admin, path: "/", component: AdminOverviewPage }),
  createRoute({ getParentRoute: () => admin, path: "/traffic", component: AdminTrafficPage }),
  createRoute({ getParentRoute: () => admin, path: "/users", component: AdminUsersPage }),
  createRoute({ getParentRoute: () => admin, path: "/users/$userId", component: AdminUserPage }),
  createRoute({ getParentRoute: () => admin, path: "/orgs", component: AdminOrgsPage }),
  createRoute({ getParentRoute: () => admin, path: "/orgs/$orgId", component: AdminOrgPage }),
  createRoute({ getParentRoute: () => admin, path: "/jobs", component: AdminJobsPage }),
  createRoute({ getParentRoute: () => admin, path: "/alerts", component: AdminAlertsPage }),
  createRoute({ getParentRoute: () => admin, path: "/backups", component: AdminBackupsPage }),
  createRoute({
    getParentRoute: () => admin,
    path: "/cancellations",
    component: AdminCancellationsPage,
  }),
  createRoute({ getParentRoute: () => admin, path: "/audit", component: AdminAuditPage }),
  createRoute({ getParentRoute: () => admin, path: "/legal", component: AdminLegalPage }),
];

// Organization -------------------------------------------------------------
function OrgLayout() {
  const { orgId, documentId } = useParams({ strict: false }) as {
    orgId: string;
    documentId?: string;
  };
  useEffect(() => rememberOrg(orgId), [orgId]);
  // The editor uses the whole window.
  if (documentId) return <Outlet />;
  return (
    <AppShell orgId={orgId}>
      <Outlet />
    </AppShell>
  );
}

const org = createRoute({
  getParentRoute: () => appLayout,
  path: "/o/$orgId",
  beforeLoad: async ({ params }) => {
    const orgs = await queryClient.fetchQuery(orpc.orgs.list.queryOptions());
    if (!orgs.some((item) => item.id === params.orgId)) throw redirect({ to: "/" });
  },
  component: OrgLayout,
});
const projects = createRoute({ getParentRoute: () => org, path: "/", component: ProjectsPage });
const project = createRoute({
  getParentRoute: () => org,
  path: "/projects/$projectId",
  component: ProjectPage,
});
const document = createRoute({
  getParentRoute: () => org,
  path: "/documents/$documentId",
  component: DocumentPage,
  // `comment`: open the comments at this thread (links in notification mails).
  validateSearch: (search: Record<string, unknown>): { comment?: string } =>
    typeof search.comment === "string" ? { comment: search.comment } : {},
});
const media = createRoute({
  getParentRoute: () => org,
  path: "/media",
  component: MediaPage,
  // Open folder of the media library (none = top level); `q` + `open` from the search.
  validateSearch: (
    search: Record<string, unknown>,
  ): { folder?: string; q?: string; open?: string } => ({
    ...(typeof search.folder === "string" ? { folder: search.folder } : {}),
    ...(typeof search.q === "string" ? { q: search.q } : {}),
    ...(typeof search.open === "string" ? { open: search.open } : {}),
  }),
});
const brand = createRoute({
  getParentRoute: () => org,
  path: "/brand",
  component: BrandPage,
  validateSearch: (search: Record<string, unknown>): { kit?: string } =>
    typeof search.kit === "string" ? { kit: search.kit } : {},
});
const templates = createRoute({
  getParentRoute: () => org,
  path: "/templates",
  component: TemplatesPage,
  // From the search: open "use template" for an own (`use`) or a built-in template (`library`).
  validateSearch: (search: Record<string, unknown>): { use?: string; library?: string } => ({
    ...(typeof search.use === "string" ? { use: search.use } : {}),
    ...(typeof search.library === "string" ? { library: search.library } : {}),
  }),
});
const planning = createRoute({
  getParentRoute: () => org,
  path: "/planning",
  component: PlanningPage,
  // `plan`: open „Beitrag planen“ with this file linked (editor menu „Einplanen …“).
  validateSearch: (search: Record<string, unknown>): { plan?: string } =>
    typeof search.plan === "string" ? { plan: search.plan } : {},
});
const trash = createRoute({ getParentRoute: () => org, path: "/trash", component: TrashPage });
const settings = createRoute({
  getParentRoute: () => org,
  path: "/settings",
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  share,
  imprint,
  privacy,
  terms,
  withdrawal,
  cancellation,
  authLayout.addChildren([signIn, signUp, forgotPassword, resetPassword]),
  cardLayout.addChildren([consent, acceptInvitation, onboarding]),
  appLayout.addChildren([
    home,
    account,
    admin.addChildren(adminRoutes),
    org.addChildren([
      projects,
      project,
      document,
      media,
      brand,
      templates,
      planning,
      trash,
      settings,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
